import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import OpportunityStatusPipeline from '../components/OpportunityStatusPipeline';
import EditROIModal from '../components/EditROIModal';
import IAODisplay from '../components/IAODisplay';
import { BACKEND_URL } from '../config';
import { useOrganization } from '../context/OrganizationContext';
import { fetchWithOrg } from '../utils/api';
import toast from '../utils/toast';
import { trackOpportunityViewed } from '../lib/posthog';
import { usePlan } from '../context/PlanContext';
import featureFlags from '../config/featureFlags';

import {
  ArrowLeft,
  ArrowRight,
  ArrowSquareOut,
  ArrowsClockwise,
  Brain,
  Calendar,
  CaretDown,
  ChatText,
  CheckCircle,
  CircleNotch,
  Clock,
  CurrencyDollar,
  EnvelopeSimple,
  GearSix,
  Info,
  Lightbulb,
  Lightning,
  Link,
  PencilSimple,
  Play,
  Plugs,
  PlugsConnected,
  Pulse,
  Robot,
  Sparkle,
  Target,
  Timer,
  TrendUp,
  UsersThree,
  Warning,
  WarningCircle
} from '@phosphor-icons/react';
const OpportunityDetail = () => {
  const { opportunityId } = useParams();
  const navigate = useNavigate();
  const { activeOrganization } = useOrganization();
  const { tier: userPlan, planLimits } = usePlan();
  
  const [opportunity, setOpportunity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  
  // ROI editing state
  const [showEditROIModal, setShowEditROIModal] = useState(false);
  
  const fetchOpportunity = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/process/opportunities/${opportunityId}`,
        {},
        activeOrganization?.organization_id
      );

      if (response.ok) {
        const data = await response.json();
        const opportunityData = data.opportunity || data;
        console.log('OpportunityDetail - Fetched opportunity:', opportunityData);
        setOpportunity(opportunityData);
        
        trackOpportunityViewed({
          opportunity_id: opportunityId,
          opportunity_title: opportunityData.title,
          plan_tier: userPlan || 'basic',
        });
      } else {
        throw new Error('Failed to fetch opportunity');
      }
    } catch (err) {
      console.error('Error fetching opportunity:', err);
      setError('Failed to load opportunity details. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [activeOrganization, opportunityId, userPlan]);

  useEffect(() => {
    if (activeOrganization && opportunityId) {
      fetchOpportunity();
    }
  }, [activeOrganization, opportunityId, fetchOpportunity]);

  const handleBuildAgent = () => {
    const canCreateDraftAgents = planLimits?.max_draft_agents > 0;
    const canCreateDeployedAgents = planLimits?.max_agents > 0;
    
    if (!canCreateDraftAgents && !canCreateDeployedAgents) {
      toast.error('Plan Limit', 'Your current plan does not allow creating agents. Please upgrade to Pro or Partner Pro.');
      navigate('/pricing');
      return;
    }
    
    // Navigate to new workflow builder with opportunity context
    navigate(`/workers/new?from=opportunity&sourceId=${opportunityId}`);
  };

  const [installingSystem, setInstallingSystem] = useState(false);

  const handleInstallSystem = async () => {
    const systemId = opportunity?.suggested_system_id;
    if (!systemId) return;

    try {
      setInstallingSystem(true);
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/agent-templates/${systemId}/instantiate`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) }
      );
      const data = await response.json();
      if (data?.agent_id) {
        toast.success('System installed', `${opportunity.suggested_system?.name || 'System'} is now active.`);
        navigate(`/workers/${data.agent_id}`);
      } else {
        throw new Error(data?.detail || 'Install failed');
      }
    } catch (err) {
      toast.error('Install failed', err.message || 'Could not install system.');
    } finally {
      setInstallingSystem(false);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    try {
      setUpdating(true);
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/process/opportunities/${opportunityId}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: newStatus })
        },
        activeOrganization?.organization_id
      );

      if (response.ok) {
        setOpportunity(prev => ({ 
          ...prev, 
          status: newStatus,
          updated_at: new Date().toISOString()
        }));
      } else {
        throw new Error('Failed to update status');
      }
    } catch (err) {
      console.error('Error updating status:', err);
      setError('Failed to update status. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const handleSaveROI = async (updatedROI) => {
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/process/opportunities/${opportunityId}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ roi: updatedROI })
        },
        activeOrganization?.organization_id
      );

      if (response.ok) {
        // Update opportunity with new ROI
        setOpportunity(prev => ({
          ...prev,
          roi: updatedROI,
          updated_at: new Date().toISOString()
        }));
        console.log('✅ ROI estimates updated successfully');
      } else {
        throw new Error('Failed to update ROI');
      }
    } catch (err) {
      console.error('Error saving ROI:', err);
      throw err;
    }
  };

  // Check if opportunity is missing business clarity fields
  const isMissingBusinessClarity = () => {
    if (!opportunity) return false;
    
    const missing = [];
    if (!opportunity.one_liner) missing.push('one_liner');
    if (!opportunity.expected_outcomes || opportunity.expected_outcomes.length < 3) missing.push('expected_outcomes');
    if (!opportunity.workflow_fit || !opportunity.workflow_fit.primary_users || opportunity.workflow_fit.primary_users.length < 2) missing.push('workflow_fit');
    if (!opportunity.example_inputs || opportunity.example_inputs.length < 2) missing.push('example_inputs');
    if (!opportunity.example_outputs || opportunity.example_outputs.length < 2) missing.push('example_outputs');
    if (!opportunity.confidence_notes || opportunity.confidence_notes.length < 120) missing.push('confidence_notes');
    
    return missing.length > 0 ? missing : false;
  };

  // Regenerate opportunity with business clarity
  const handleRegenerateOpportunity = async () => {
    try {
      setRegenerating(true);
      
      // Use the new regenerate-v1 endpoint that pulls source evidence
      const regenResponse = await fetchWithOrg(
        `${BACKEND_URL}/api/opportunities/${opportunityId}/regenerate-v1`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        },
        activeOrganization?.organization_id
      );

      const data = await regenResponse.json();
      
      if (regenResponse.ok && data.success) {
        // Update opportunity with regenerated V1-compliant data
        setOpportunity(prev => ({
          ...prev,
          ...data.opportunity,
          updated_at: new Date().toISOString()
        }));
        toast.success('Opportunity Regenerated', 'This opportunity is now V1 compliant with full business clarity.');
      } else if (data.error === 'REGENERATION_FAILED') {
        // Show detailed error info
        const errorDetails = data.last_errors?.slice(0, 2).join(', ') || 'Unknown error';
        toast.error('Regeneration Failed', `Could not generate valid V1 opportunity: ${errorDetails}`);
      } else if (data.error === 'MISSING_EVIDENCE') {
        // Handle missing source evidence
        toast.warning('Limited Evidence', 'Could not find original source evidence. Regenerating with available data.');
        // Try fallback to upgrade-to-v1
        const fallbackResponse = await fetchWithOrg(
          `${BACKEND_URL}/api/opportunities/${opportunityId}/upgrade-to-v1`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
          },
          activeOrganization?.organization_id
        );
        if (fallbackResponse.ok) {
          const fallbackData = await fallbackResponse.json();
          if (fallbackData.success && fallbackData.opportunity) {
            setOpportunity(prev => ({
              ...prev,
              ...fallbackData.opportunity,
              updated_at: new Date().toISOString()
            }));
            toast.success('Opportunity Updated', 'Business clarity fields have been added.');
          }
        }
      } else {
        const errorMsg = data.detail || data.message || 'Could not regenerate opportunity.';
        toast.error('Regeneration Failed', errorMsg);
      }
    } catch (err) {
      console.error('Error regenerating opportunity:', err);
      toast.error('Error', 'Failed to regenerate opportunity. Please try again.');
    } finally {
      setRegenerating(false);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'text-[#00E5C0]';
      case 'medium':
        return 'text-[#F59E0B]';
      case 'low':
        return 'text-[#14F195]';
      default:
        return 'text-[#A0AEC0]';
    }
  };

  const getPriorityBgColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-[rgba(236,72,153,0.1)] border-[rgba(236,72,153,0.3)]';
      case 'medium':
        return 'bg-[rgba(245,158,11,0.1)] border-[rgba(245,158,11,0.3)]';
      case 'low':
        return 'bg-[rgba(20,241,149,0.1)] border-[rgba(20,241,149,0.3)]';
      default:
        return 'bg-[rgba(160,174,192,0.1)] border-[rgba(160,174,192,0.3)]';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'identified':
        return <WarningCircle size={20} className="text-[#00E5C0]" />;
      case 'evaluating':
        return <Pulse size={20} className="text-[#F59E0B]" />;
      case 'planning':
        return <Clock size={20} className="text-[#00D4B0]" />;
      case 'in_progress':
        return <Lightning size={20} className="text-[#14F195]" />;
      case 'implemented':
        return <CheckCircle size={20} className="text-[#14F195]" />;
      default:
        return <WarningCircle size={20} className="text-[#A0AEC0]" />;
    }
  };

  const formatStatus = (status) => {
    return status.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const statuses = [
    { value: 'identified', label: 'Identified', color: '#00E5C0' },
    { value: 'evaluating', label: 'Evaluating', color: '#F59E0B' },
    { value: 'planning', label: 'Planning', color: '#00D4B0' },
    { value: 'in_progress', label: 'In Progress', color: '#14F195' },
    { value: 'implemented', label: 'Implemented', color: '#14F195' }
  ];

  // ─── Outcome-driven headline generator ──────────────────
  const getOutcomeHeadline = () => {
    const o = opportunity;
    if (!o) return '';

    // 1) If one_liner exists and reads like a benefit statement
    if (o.one_liner && o.one_liner.length > 15 && /save|improv|reduc|automat|faster|increas|boost|eliminat|streamlin/i.test(o.one_liner)) {
      return o.one_liner;
    }

    // 2) Build from expected_outcomes if available
    if (o.expected_outcomes?.length > 0) {
      const firstOutcome = typeof o.expected_outcomes[0] === 'string' ? o.expected_outcomes[0] : '';
      if (firstOutcome) {
        // Extract core benefit from first outcome
        const cleaned = firstOutcome.replace(/^(this |the |it )/i, '').trim();
        const capped = cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
        return capped;
      }
    }

    // 3) Construct from title + description
    const title = o.title || '';
    const roi = o.roi?.roi_percentage;
    if (roi && roi > 0) {
      return `${title} — save up to ${roi.toFixed(0)}% of current effort`;
    }

    // 4) Fallback: use title with benefit framing
    if (o.one_liner) return o.one_liner;
    return title;
  };

  // ─── "How it works" — max 20 words, plain English ──────
  const getHowItWorks = () => {
    const o = opportunity;
    if (!o) return '';

    const trigger = o.workflow_fit?.trigger;
    const triggerText = typeof trigger === 'string' ? trigger : '';
    const desc = typeof o.description === 'string' ? o.description : '';
    const title = o.title || '';

    // Build: "This worker monitors X and automatically does Y"
    let whenPart = 'new activity arrives';
    if (triggerText) {
      whenPart = triggerText.toLowerCase().replace(/^(when |whenever |on )/i, '').replace(/\.$/, '');
    }

    let whatPart = title.toLowerCase();
    if (desc && desc.length < 100) {
      whatPart = desc.toLowerCase();
    }

    const sentence = `This worker monitors when ${whenPart} and automatically handles ${whatPart}.`;
    // Enforce 20-word max
    const words = sentence.split(/\s+/);
    if (words.length <= 20) return sentence;
    return words.slice(0, 20).join(' ') + '…';
  };

  // ─── "When it runs" — human-readable trigger ──────
  const getWhenItRuns = () => {
    const trigger = opportunity?.workflow_fit?.trigger;
    if (!trigger) return null;
    const text = typeof trigger === 'string' ? trigger : JSON.stringify(trigger);
    // Ensure it starts with "Runs"
    if (/^runs/i.test(text)) return text;
    return `Runs whenever ${text.charAt(0).toLowerCase() + text.slice(1)}`;
  };

  // ─── Tools required — infer from capabilities/integration metadata ──────
  const getRequiredTools = () => {
    const tools = [];
    const caps = opportunity?.required_capabilities || [];
    const optCaps = opportunity?.optional_capabilities || [];
    const intNotes = opportunity?.integration_notes || '';
    const desc = typeof opportunity?.description === 'string' ? opportunity.description.toLowerCase() : '';
    const title = (opportunity?.title || '').toLowerCase();
    const combined = `${desc} ${title} ${intNotes.toLowerCase()} ${caps.join(' ').toLowerCase()} ${optCaps.join(' ').toLowerCase()}`;

    // Email
    if (/email|gmail|outlook|inbox|mail/i.test(combined)) {
      tools.push({ name: 'Email (Gmail / Outlook)', key: 'email', required: true, icon: 'email' });
    }
    // Calendar
    if (/calendar|schedul|book|appointment/i.test(combined)) {
      tools.push({ name: 'Calendar (Google / Outlook)', key: 'calendar', required: /calendar|schedul/i.test(caps.join(' ')), icon: 'calendar' });
    }
    // CRM
    if (/crm|salesforce|hubspot|customer.*relation|deal|pipeline/i.test(combined)) {
      tools.push({ name: 'CRM (Salesforce / HubSpot)', key: 'crm', required: false, icon: 'crm' });
    }
    // Slack
    if (/slack|team.*chat|notification/i.test(combined)) {
      tools.push({ name: 'Slack', key: 'slack', required: false, icon: 'chat' });
    }
    // SMS
    if (/sms|text.*messag|twilio/i.test(combined)) {
      tools.push({ name: 'SMS (Twilio)', key: 'sms', required: false, icon: 'sms' });
    }

    // If nothing inferred, add email as default
    if (tools.length === 0) {
      tools.push({ name: 'Email (Gmail / Outlook)', key: 'email', required: true, icon: 'email' });
    }

    return tools;
  };

  // ─── Setup time heuristic ──────
  const getSetupTime = () => {
    const effort = opportunity?.effort;
    const effortNum = typeof effort === 'number' ? effort : 5;
    if (effortNum <= 3) return 'Works instantly once connected';
    if (effortNum <= 5) return 'Takes ~5 minutes to set up';
    if (effortNum <= 7) return 'Takes ~10 minutes to configure';
    return 'Takes ~15 minutes to configure';
  };

  // ─── Advanced section toggle ──────
  const [showAdvanced, setShowAdvanced] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#161B26] flex items-center justify-center">
        <CircleNotch size={48} className="text-[#00E5C0] animate-spin" />
      </div>
    );
  }

  if (error || !opportunity) {
    return (
        <div className="max-w-7xl mx-auto">
          <div className="glass rounded-2xl p-12 text-center">
            <WarningCircle size={64} className="mx-auto mb-6 text-[#EF4444]" />
            <h2 className="text-2xl font-bold text-white mb-4">Error Loading Opportunity</h2>
            <p className="text-[#A0AEC0] mb-8">{error || 'Opportunity not found'}</p>
            <button
              onClick={() => navigate(-1)}
              className="px-6 py-3 rounded-full bg-[#00E5C0] text-white font-semibold hover:opacity-90 transition-opacity"
            >
              Back to Opportunities
            </button>
          </div>
        </div>
      
    );
  }

  return (
      <>
      <div className="max-w-4xl mx-auto" data-testid="opportunity-detail">
          {/* Back Button */}
          <button
            onClick={() => navigate(-1)}
            data-testid="back-to-opportunities"
            className="flex items-center gap-2 text-[#B0B8C4] hover:text-white mb-6 transition-colors text-sm"
          >
            <ArrowLeft size={16} />
            Back to Opportunities
          </button>

          {/* ═══════════════ SECTION 1 — HEADLINE ═══════════════ */}
          <div className="mb-6" data-testid="opportunity-headline-section">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              {/* Status Badge */}
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[rgba(255,255,255,0.06)] border border-[rgba(255,255,255,0.08)]">
                {getStatusIcon(opportunity.status)}
                <span className="text-white text-xs font-medium">
                  {formatStatus(opportunity.status)}
                </span>
              </div>
              {/* Priority */}
              {opportunity.priority_level && (
                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${getPriorityBgColor(opportunity.priority_level)} ${getPriorityColor(opportunity.priority_level)}`}>
                  {opportunity.priority_level.toUpperCase()}
                </span>
              )}
              {/* Linked Worker */}
              {opportunity.linked_agent_id && (
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[rgba(0,229,192,0.08)] border border-[rgba(0,229,192,0.15)]">
                  <Robot size={12} className="text-[#00E5C0]" />
                  <span className="text-[#00E5C0] text-xs font-medium">Worker Linked</span>
                </div>
              )}
            </div>

            <h1 className="text-2xl font-bold text-white mb-2 leading-tight" data-testid="opportunity-headline">
              {getOutcomeHeadline()}
            </h1>

            {/* Benefit subtitle — fallback to description if headline was from one_liner */}
            {opportunity.description && (
              <p className="text-sm text-[#B0B8C4] leading-relaxed max-w-2xl" data-testid="opportunity-subtitle">
                {typeof opportunity.description === 'object'
                  ? JSON.stringify(opportunity.description)
                  : opportunity.description.length > 200
                    ? opportunity.description.slice(0, 200) + '…'
                    : opportunity.description}
              </p>
            )}
          </div>

          {/* ═══════════════ SECTION 2 — ROI CARD (UNCHANGED) ═══════════════ */}
          <div className="bg-[#1E2836] rounded-xl border border-[rgba(255,255,255,0.08)] p-5 mb-6" data-testid="roi-card">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <TrendUp size={18} className="text-[#00E5C0]" />
                <span className="text-sm font-semibold text-white">Return on Investment</span>
              </div>
              <button
                onClick={() => setShowEditROIModal(true)}
                className="p-1.5 hover:bg-[rgba(255,255,255,0.06)] rounded-lg transition-colors"
                title="Edit ROI estimates"
                data-testid="edit-roi-btn"
              >
                <PencilSimple size={14} className="text-[#7D8896]" />
              </button>
            </div>

            {opportunity.roi && opportunity.roi.status?.toLowerCase() !== 'unknown' ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-3">
                  <div>
                    <p className="text-[10px] text-[#7D8896] uppercase tracking-wider mb-1">ROI</p>
                    <p className={`text-xl font-bold ${
                      ['excellent', 'good', 'estimated'].includes(opportunity.roi.status?.toLowerCase()) ? 'text-[#00E5C0]' :
                      opportunity.roi.status?.toLowerCase() === 'moderate' ? 'text-[#F59E0B]' : 'text-[#EF4444]'
                    }`} data-testid="roi-percentage">
                      {opportunity.roi.roi_percentage !== undefined && opportunity.roi.roi_percentage !== null
                        ? `${opportunity.roi.roi_percentage.toFixed(0)}%`
                        : opportunity.roi.status || '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] text-[#7D8896] uppercase tracking-wider mb-1">Annual Savings</p>
                    <p className="text-xl font-bold text-white" data-testid="roi-savings">
                      ${(opportunity.roi.annual_savings ?? opportunity.roi.roi_annual)?.toLocaleString() || '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] text-[#7D8896] uppercase tracking-wider mb-1">Net Value</p>
                    <p className={`text-xl font-bold ${opportunity.roi.roi_value >= 0 ? 'text-[#00E5C0]' : 'text-[#EF4444]'}`} data-testid="roi-net">
                      ${opportunity.roi.roi_value?.toLocaleString() || '—'}
                    </p>
                  </div>
                </div>
                <p className="text-[10px] text-[#7D8896]">
                  ROI estimates are directional only and based on typical industry benchmarks.
                </p>
              </>
            ) : (
              <p className="text-sm text-[#7D8896]">ROI data not yet available for this opportunity.</p>
            )}
          </div>

          {/* ═══════════════ SECTION 3 — WHAT YOU'LL GET ═══════════════ */}
          {opportunity.expected_outcomes?.length > 0 && (
            <div className="bg-[#1E2836] rounded-xl border border-[rgba(255,255,255,0.08)] p-5 mb-6" data-testid="what-you-get-section">
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle size={18} weight="fill" className="text-[#00E5C0]" />
                <span className="text-sm font-semibold text-white">What you'll get</span>
              </div>
              <div className="space-y-2.5">
                {opportunity.expected_outcomes.slice(0, 5).map((outcome, idx) => (
                  <div key={idx} className="flex items-start gap-3" data-testid={`outcome-${idx}`}>
                    <div className="w-1.5 h-1.5 rounded-full bg-[#00E5C0] mt-2 flex-shrink-0" />
                    <p className="text-sm text-[#F0F4F8] leading-relaxed">
                      {typeof outcome === 'object' ? JSON.stringify(outcome) : outcome}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ═══════════════ SECTION 4 — HOW IT WORKS (ONE SENTENCE) ═══════════════ */}
          <div className="bg-[#1E2836] rounded-xl border border-[rgba(255,255,255,0.08)] p-5 mb-6" data-testid="how-it-works-section">
            <div className="flex items-center gap-2 mb-3">
              <Lightning size={18} weight="fill" className="text-[#F59E0B]" />
              <span className="text-sm font-semibold text-white">How it works</span>
            </div>
            <p className="text-sm text-[#B0B8C4] leading-relaxed" data-testid="how-it-works-text">
              {getHowItWorks()}
            </p>
          </div>

          {/* ═══════════════ SECTION 5 — WHEN IT RUNS ═══════════════ */}
          {getWhenItRuns() && (
            <div className="bg-[#1E2836] rounded-xl border border-[rgba(255,255,255,0.08)] p-5 mb-6" data-testid="when-it-runs-section">
              <div className="flex items-center gap-2 mb-3">
                <Play size={18} weight="fill" className="text-[#818CF8]" />
                <span className="text-sm font-semibold text-white">When it runs</span>
              </div>
              <p className="text-sm text-[#B0B8C4]" data-testid="when-it-runs-text">
                {getWhenItRuns()}
              </p>
            </div>
          )}

          {/* ═══════════════ VISUAL WORKFLOW ═══════════════ */}
          {(() => {
            const trigger = opportunity?.workflow_fit?.trigger;
            const triggerText = typeof trigger === 'string' ? trigger : 'New activity detected';
            const tools = getRequiredTools();
            const title = opportunity?.title || 'Process';
            const outcomes = opportunity?.expected_outcomes?.slice(0, 2) || [];

            // Build workflow steps dynamically
            const steps = [
              { label: 'Trigger', detail: triggerText.length > 40 ? triggerText.slice(0, 40) + '...' : triggerText, color: '#818CF8' },
              { label: 'Connect', detail: tools.map(t => t.name.split(' (')[0]).join(', ') || 'Email', color: '#00E5C0' },
              { label: 'Process', detail: title.length > 40 ? title.slice(0, 40) + '...' : title, color: '#F59E0B' },
              { label: 'Output', detail: outcomes.length > 0 ? (typeof outcomes[0] === 'string' ? outcomes[0].slice(0, 40) : 'Result') : 'Automated result', color: '#14F195' },
            ];

            return (
              <div className="bg-[#1E2836] rounded-xl border border-[rgba(255,255,255,0.08)] p-5 mb-6" data-testid="visual-workflow-section">
                <div className="flex items-center gap-2 mb-4">
                  <Lightning size={18} className="text-[#00E5C0]" />
                  <span className="text-sm font-semibold text-white">Workflow</span>
                </div>
                <div className="flex items-stretch gap-0 overflow-x-auto pb-1">
                  {steps.map((step, idx) => (
                    <div key={idx} className="flex items-center min-w-0 flex-1">
                      <div className="flex flex-col items-center text-center flex-1 min-w-[80px]">
                        <div
                          className="w-9 h-9 rounded-lg flex items-center justify-center mb-2"
                          style={{ backgroundColor: `${step.color}12`, border: `1px solid ${step.color}30` }}
                        >
                          <span className="text-xs font-bold" style={{ color: step.color }}>{idx + 1}</span>
                        </div>
                        <p className="text-[11px] font-semibold text-white mb-0.5">{step.label}</p>
                        <p className="text-[10px] text-[#7D8896] leading-tight px-1 line-clamp-2">{step.detail}</p>
                      </div>
                      {idx < steps.length - 1 && (
                        <div className="flex items-center shrink-0 -mx-1 self-start mt-4">
                          <ArrowRight size={14} className="text-[#3A4050]" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* ═══════════════ SECTION 6 — TOOLS REQUIRED ═══════════════ */}
          <div className="bg-[#1E2836] rounded-xl border border-[rgba(255,255,255,0.08)] p-5 mb-6" data-testid="tools-required-section">
            <div className="flex items-center gap-2 mb-4">
              <PlugsConnected size={18} className="text-[#00E5C0]" />
              <span className="text-sm font-semibold text-white">Tools required</span>
            </div>
            <div className="space-y-2">
              {getRequiredTools().map((tool) => (
                <div
                  key={tool.key}
                  className="flex items-center justify-between px-3 py-2.5 rounded-lg bg-[#1F2937] border border-[rgba(255,255,255,0.04)]"
                  data-testid={`tool-${tool.key}`}
                >
                  <div className="flex items-center gap-2.5">
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${
                      tool.required ? 'bg-[rgba(0,229,192,0.08)]' : 'bg-[rgba(255,255,255,0.04)]'
                    }`}>
                      {tool.icon === 'email' && <EnvelopeSimple size={14} className={tool.required ? 'text-[#00E5C0]' : 'text-[#7D8896]'} />}
                      {tool.icon === 'calendar' && <Calendar size={14} className={tool.required ? 'text-[#00E5C0]' : 'text-[#7D8896]'} />}
                      {tool.icon === 'crm' && <UsersThree size={14} className="text-[#7D8896]" />}
                      {tool.icon === 'chat' && <ChatText size={14} className="text-[#7D8896]" />}
                      {tool.icon === 'sms' && <ChatText size={14} className="text-[#7D8896]" />}
                    </div>
                    <div>
                      <p className="text-xs text-[#F0F4F8] font-medium">{tool.name}</p>
                      <p className="text-[10px] text-[#7D8896]">
                        {tool.required ? 'Required' : 'Optional'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); window.dispatchEvent(new CustomEvent('open-tools-modal')); }}
                    className="px-2.5 py-1 rounded-md text-[10px] font-semibold border transition-colors bg-[rgba(0,229,192,0.06)] border-[rgba(0,229,192,0.15)] text-[#00E5C0] hover:bg-[rgba(0,229,192,0.12)]"
                    data-testid={`connect-tool-${tool.key}`}
                  >
                    Connect
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* ═══════════════ SETUP TIME ═══════════════ */}
          <div className="bg-[#1E2836] rounded-xl border border-[rgba(255,255,255,0.08)] p-5 mb-6" data-testid="setup-time-section">
            <div className="flex items-center gap-2 mb-3">
              <Timer size={18} className="text-[#818CF8]" />
              <span className="text-sm font-semibold text-white">Setup time</span>
            </div>
            <p className="text-sm text-[#B0B8C4]" data-testid="setup-time-text">
              {getSetupTime()}
            </p>
          </div>

          {/* ═══════════════ SECTION 7 — WHO IT HELPS ═══════════════ */}
          {opportunity.workflow_fit?.primary_users?.length > 0 && (
            <div className="bg-[#1E2836] rounded-xl border border-[rgba(255,255,255,0.08)] p-5 mb-6" data-testid="who-it-helps-section">
              <div className="flex items-center gap-2 mb-4">
                <UsersThree size={18} className="text-[#F59E0B]" />
                <span className="text-sm font-semibold text-white">Who it helps</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {opportunity.workflow_fit.primary_users.map((user, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1.5 rounded-lg bg-[rgba(245,158,11,0.06)] border border-[rgba(245,158,11,0.12)] text-[#FBBF24] text-xs font-medium"
                    data-testid={`role-chip-${idx}`}
                  >
                    {typeof user === 'object' ? JSON.stringify(user) : user}
                  </span>
                ))}
                {/* Also show affected roles if available */}
                {opportunity.affected_roles?.map((role, idx) => (
                  <span
                    key={`af-${idx}`}
                    className="px-3 py-1.5 rounded-lg bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] text-[#B0B8C4] text-xs font-medium"
                  >
                    {role}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* ═══════════════ SECTION 8 — CTA ═══════════════ */}
          <div className="bg-[#1E2836] rounded-xl border border-[rgba(0,229,192,0.12)] p-6 mb-6" data-testid="cta-section">
            {opportunity.linked_agent_id ? (
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[rgba(0,229,192,0.08)] flex items-center justify-center">
                    <Robot size={20} className="text-[#00E5C0]" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white">Worker linked</p>
                    <p className="text-xs text-[#B0B8C4]">{opportunity.linked_agent_name || 'AI Worker'}</p>
                  </div>
                </div>
                <button
                  onClick={() => navigate(`/workers/${opportunity.linked_agent_id}`)}
                  data-testid="open-worker-btn"
                  className="px-5 py-2.5 rounded-lg bg-[#00E5C0] text-[#12161C] text-sm font-bold hover:bg-[#00D4B0] transition-colors flex items-center gap-2"
                >
                  Open Worker
                  <ArrowSquareOut size={14} weight="bold" />
                </button>
              </div>
            ) : featureFlags.SYSTEM_LINKING_V1 && opportunity.match_type === 'deterministic' && opportunity.suggested_system ? (
              /* ── Deterministic System Match ── */
              <div data-testid="system-match-section">
                {/* System Card */}
                <div className="border border-[rgba(0,229,192,0.12)] rounded-xl bg-[rgba(0,229,192,0.03)] p-4 mb-4">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-[rgba(0,229,192,0.1)] flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Lightning size={18} className="text-[#00E5C0]" weight="fill" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-white" data-testid="system-match-name">
                        {opportunity.suggested_system.name}
                      </p>
                      <p className="text-xs text-[#B0B8C4] mt-0.5 line-clamp-2" data-testid="system-match-desc">
                        {opportunity.suggested_system.short_description || opportunity.suggested_system.description?.slice(0, 120)}
                      </p>
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${
                          opportunity.suggested_system.execution_mode === 'automated'
                            ? 'bg-[rgba(16,185,129,0.1)] text-[#10B981] border border-[rgba(16,185,129,0.2)]'
                            : opportunity.suggested_system.execution_mode === 'assisted'
                            ? 'bg-[rgba(245,158,11,0.1)] text-[#F59E0B] border border-[rgba(245,158,11,0.2)]'
                            : 'bg-[rgba(99,102,241,0.1)] text-[#818CF8] border border-[rgba(99,102,241,0.2)]'
                        }`} data-testid="system-execution-mode">
                          {opportunity.suggested_system.execution_mode === 'automated' ? 'Fully automated' :
                           opportunity.suggested_system.execution_mode === 'assisted' ? 'Assisted (review required)' :
                           'Integration required'}
                        </span>
                        {(opportunity.suggested_system.integration_requirements?.required || []).map((app, i) => (
                          <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-[rgba(255,255,255,0.04)] text-[#B0B8C4] border border-[rgba(255,255,255,0.08)]" data-testid={`system-integration-${i}`}>
                            <Plugs size={10} /> {app}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Readiness indicator */}
                {opportunity.system_readiness && (
                  <div className={`flex items-center gap-2 mb-4 px-3 py-2 rounded-lg text-xs ${
                    opportunity.system_readiness.ready
                      ? 'bg-[rgba(16,185,129,0.06)] text-[#10B981]'
                      : 'bg-[rgba(245,158,11,0.06)] text-[#F59E0B]'
                  }`} data-testid="system-readiness">
                    {opportunity.system_readiness.ready ? (
                      <>
                        <CheckCircle size={14} weight="fill" />
                        <span>Ready to install</span>
                      </>
                    ) : (
                      <>
                        <Warning size={14} weight="fill" />
                        <span>
                          Requires{' '}
                          {(opportunity.system_readiness.missing_integrations || []).join(', ')} connection
                        </span>
                      </>
                    )}
                  </div>
                )}

                {/* CTA */}
                <button
                  onClick={handleInstallSystem}
                  disabled={installingSystem || isMissingBusinessClarity()}
                  data-testid="install-system-btn"
                  className={`w-full px-8 py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-colors ${
                    installingSystem || isMissingBusinessClarity()
                      ? 'bg-[#2A2F36] text-[#7D8896] cursor-not-allowed'
                      : 'bg-[#00E5C0] text-[#12161C] hover:bg-[#00D4B0]'
                  }`}
                >
                  {installingSystem ? (
                    <><CircleNotch size={16} className="animate-spin" /> Installing...</>
                  ) : (
                    <><Lightning size={16} weight="bold" /> Set Up This System</>
                  )}
                </button>
              </div>
            ) : (
              /* ── Fallback: existing LLM-based CTA ── */
              <div className="text-center">
                <p className="text-sm text-[#B0B8C4] mb-4">
                  Turn this opportunity into an active worker on your team.
                </p>
                <button
                  onClick={handleBuildAgent}
                  disabled={isMissingBusinessClarity()}
                  data-testid="setup-worker-btn"
                  className={`px-8 py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 mx-auto transition-colors ${
                    isMissingBusinessClarity()
                      ? 'bg-[#2A2F36] text-[#7D8896] cursor-not-allowed'
                      : 'bg-[#00E5C0] text-[#12161C] hover:bg-[#00D4B0]'
                  }`}
                >
                  <Sparkle size={16} weight="bold" />
                  Set up this worker
                </button>
                {isMissingBusinessClarity() && (
                  <p className="text-[10px] text-[#7D8896] mt-2">
                    Regenerate this opportunity to add required details before setup.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* ═══════════════ STATUS PIPELINE (PRESERVED) ═══════════════ */}
          <div className="bg-[#1E2836] rounded-xl border border-[rgba(255,255,255,0.08)] p-5 mb-6" data-testid="status-pipeline-section">
            <h3 className="text-sm font-semibold text-white mb-4">Pipeline Status</h3>
            <OpportunityStatusPipeline
              status={opportunity.status}
              onStatusChange={handleStatusUpdate}
            />
          </div>

          {/* ═══════════════ BUSINESS CLARITY WARNING ═══════════════ */}
          {isMissingBusinessClarity() && (
            <div className="bg-[rgba(245,158,11,0.04)] rounded-xl border border-[rgba(245,158,11,0.15)] p-5 mb-6" data-testid="clarity-warning">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <Warning size={18} className="text-[#F59E0B] flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-semibold text-[#F59E0B] mb-1">Needs more detail</h3>
                    <p className="text-xs text-[#B0B8C4]">
                      Missing fields: {isMissingBusinessClarity().join(', ')}
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleRegenerateOpportunity}
                  disabled={regenerating}
                  data-testid="regenerate-btn"
                  className="px-4 py-2 rounded-lg bg-[#F59E0B] hover:bg-[#D97706] text-[#0A0F1A] text-xs font-bold flex items-center gap-2 transition-colors flex-shrink-0 disabled:opacity-50"
                >
                  {regenerating ? (
                    <><CircleNotch size={14} className="animate-spin" /> Regenerating…</>
                  ) : (
                    <><ArrowsClockwise size={14} /> Regenerate</>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* ═══════════════ ADVANCED (COLLAPSIBLE) ═══════════════ */}
          <div className="mb-6" data-testid="advanced-section">
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              data-testid="toggle-advanced"
              className="w-full flex items-center justify-between px-5 py-3.5 rounded-xl bg-[#1E2836] border border-[rgba(255,255,255,0.08)] text-sm font-medium text-[#B0B8C4] hover:text-white transition-colors"
            >
              <span className="flex items-center gap-2">
                <GearSix size={16} />
                Advanced Details
              </span>
              <CaretDown
                size={14}
                className={`transition-transform duration-200 ${showAdvanced ? 'rotate-180' : ''}`}
              />
            </button>

            {showAdvanced && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-3 space-y-4"
              >
                {/* Scoring Metrics */}
                <div className="bg-[#1E2836] rounded-xl border border-[rgba(255,255,255,0.08)] p-5">
                  <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                    <Target size={16} className="text-[#818CF8]" />
                    Scoring Breakdown
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div>
                      <p className="text-[10px] text-[#7D8896] uppercase tracking-wider mb-1">Impact</p>
                      <p className="text-lg font-bold text-white">{opportunity.impact || 5}/10</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-[#7D8896] uppercase tracking-wider mb-1">Effort</p>
                      <p className="text-lg font-bold text-white">{opportunity.effort || 5}/10</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-[#7D8896] uppercase tracking-wider mb-1">Priority</p>
                      <p className={`text-lg font-bold ${getPriorityColor(opportunity.priority_level)}`}>
                        {(opportunity.priority_score || 50).toFixed(0)}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] text-[#7D8896] uppercase tracking-wider mb-1">Confidence</p>
                      <p className={`text-lg font-bold ${
                        (opportunity.confidence_score || 50) >= 70 ? 'text-[#10B981]' :
                        (opportunity.confidence_score || 50) >= 60 ? 'text-[#F59E0B]' : 'text-[#EF4444]'
                      }`}>
                        {opportunity.confidence_score || 50}%
                      </p>
                    </div>
                  </div>

                  {/* Scoring detail breakdown */}
                  {opportunity.scoring_breakdown && (
                    <div className="mt-4 pt-4 border-t border-[rgba(255,255,255,0.08)]">
                      <div className="grid md:grid-cols-3 gap-4">
                        {opportunity.scoring_breakdown.impact_dimensions?.length > 0 && (
                          <div>
                            <h4 className="text-[10px] font-semibold text-[#818CF8] uppercase mb-2">Impact Drivers</h4>
                            <ul className="space-y-1">
                              {opportunity.scoring_breakdown.impact_dimensions.map((dim, idx) => (
                                <li key={idx} className="text-[11px] text-[#B0B8C4]">• {dim.explanation}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {opportunity.scoring_breakdown.effort_factors?.length > 0 && (
                          <div>
                            <h4 className="text-[10px] font-semibold text-[#F59E0B] uppercase mb-2">Effort Factors</h4>
                            <ul className="space-y-1">
                              {opportunity.scoring_breakdown.effort_factors.map((f, idx) => (
                                <li key={idx} className="text-[11px] text-[#B0B8C4]">• {f.explanation}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {opportunity.scoring_breakdown.confidence_factors?.length > 0 && (
                          <div>
                            <h4 className="text-[10px] font-semibold text-[#10B981] uppercase mb-2">Confidence</h4>
                            <ul className="space-y-1">
                              {opportunity.scoring_breakdown.confidence_factors.map((f, idx) => (
                                <li key={idx} className={`text-[11px] ${f.positive ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                                  • {f.name}: {f.impact}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Examples: Inputs / Outputs */}
                {(opportunity.example_inputs?.length > 0 || opportunity.example_outputs?.length > 0) && (
                  <div className="bg-[#1E2836] rounded-xl border border-[rgba(255,255,255,0.08)] p-5">
                    <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                      <Lightbulb size={16} className="text-[#F59E0B]" />
                      Example Inputs & Outputs
                    </h3>
                    <div className="grid md:grid-cols-2 gap-6">
                      {opportunity.example_inputs?.length > 0 && (
                        <div>
                          <p className="text-[10px] font-semibold text-[#818CF8] uppercase tracking-wider mb-2">Inputs</p>
                          <div className="space-y-2">
                            {opportunity.example_inputs.map((ex, idx) => (
                              <div key={idx} className="pl-3 border-l-2 border-[rgba(129,140,248,0.2)]">
                                <p className="text-[11px] text-[#B0B8C4]">{typeof ex === 'object' ? JSON.stringify(ex) : ex}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {opportunity.example_outputs?.length > 0 && (
                        <div>
                          <p className="text-[10px] font-semibold text-[#00E5C0] uppercase tracking-wider mb-2">Outputs</p>
                          <div className="space-y-2">
                            {opportunity.example_outputs.map((ex, idx) => (
                              <div key={idx} className="pl-3 border-l-2 border-[rgba(0,229,192,0.2)]">
                                <p className="text-[11px] text-[#B0B8C4]">{typeof ex === 'object' ? JSON.stringify(ex) : ex}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* IAO Flow Breakdown */}
                {opportunity.iao && (
                  <div className="bg-[#1E2836] rounded-xl border border-[rgba(255,255,255,0.08)] p-5">
                    <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                      <Brain size={16} className="text-[#00D4B0]" />
                      Flow Breakdown (IAO)
                    </h3>
                    <IAODisplay opportunity={opportunity} />
                  </div>
                )}

                {/* Assumptions & Confidence Notes */}
                {opportunity.confidence_notes && (
                  <div className="bg-[#1E2836] rounded-xl border border-[rgba(255,255,255,0.08)] p-5">
                    <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                      <Info size={16} className="text-[#7D8896]" />
                      Assumptions
                    </h3>
                    <p className="text-[11px] text-[#B0B8C4] leading-relaxed">
                      {typeof opportunity.confidence_notes === 'object'
                        ? JSON.stringify(opportunity.confidence_notes)
                        : opportunity.confidence_notes}
                    </p>
                    {opportunity.roi_assumptions && (
                      <div className="mt-3 grid grid-cols-3 gap-3 text-[11px]">
                        {opportunity.roi_assumptions.hours_saved_basis && (
                          <div>
                            <span className="text-[#7D8896]">Hours Saved Basis:</span>
                            <p className="text-[#B0B8C4]">{typeof opportunity.roi_assumptions.hours_saved_basis === 'object' ? JSON.stringify(opportunity.roi_assumptions.hours_saved_basis) : opportunity.roi_assumptions.hours_saved_basis}</p>
                          </div>
                        )}
                        {opportunity.roi_assumptions.hourly_rate_source && (
                          <div>
                            <span className="text-[#7D8896]">Rate Source:</span>
                            <p className="text-[#B0B8C4]">{typeof opportunity.roi_assumptions.hourly_rate_source === 'object' ? JSON.stringify(opportunity.roi_assumptions.hourly_rate_source) : opportunity.roi_assumptions.hourly_rate_source}</p>
                          </div>
                        )}
                        {opportunity.roi_assumptions.confidence_level && (
                          <div>
                            <span className="text-[#7D8896]">Confidence:</span>
                            <p className="text-[#B0B8C4] capitalize">{typeof opportunity.roi_assumptions.confidence_level === 'object' ? JSON.stringify(opportunity.roi_assumptions.confidence_level) : opportunity.roi_assumptions.confidence_level}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Capability Mapping */}
                {(opportunity.required_capabilities?.length > 0 || opportunity.optional_capabilities?.length > 0) && (
                  <div className="bg-[#1E2836] rounded-xl border border-[rgba(255,255,255,0.08)] p-5">
                    <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                      <Plugs size={16} className="text-[#00E5C0]" />
                      Capability Mapping
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {opportunity.required_capabilities?.map((cap, idx) => (
                        <span key={`r-${idx}`} className="px-2.5 py-1 rounded-md bg-[rgba(0,229,192,0.06)] border border-[rgba(0,229,192,0.1)] text-[#00E5C0] text-[10px] font-medium">
                          {cap}
                        </span>
                      ))}
                      {opportunity.optional_capabilities?.map((cap, idx) => (
                        <span key={`o-${idx}`} className="px-2.5 py-1 rounded-md bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] text-[#7D8896] text-[10px] font-medium">
                          {cap}
                        </span>
                      ))}
                    </div>
                    {opportunity.integration_notes && (
                      <p className="text-[10px] text-[#7D8896] mt-2">{opportunity.integration_notes}</p>
                    )}
                  </div>
                )}

                {/* Linked Source */}
                {(opportunity.process_id || opportunity.jd_id || opportunity.role_id || opportunity.org_chart_id) && (
                  <div className="bg-[#1E2836] rounded-xl border border-[rgba(255,255,255,0.08)] p-5">
                    <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                      <Link size={16} className="text-[#818CF8]" />
                      Source
                    </h3>
                    <div className="flex items-center justify-between">
                      <div>
                        {opportunity.process_id && <p className="text-xs text-[#B0B8C4]">From: <span className="text-white">{opportunity.process_name || 'Process'}</span></p>}
                        {opportunity.jd_id && <p className="text-xs text-[#B0B8C4]">From: <span className="text-white">{opportunity.jd_title || 'Job Description'}</span></p>}
                        {opportunity.role_id && <p className="text-xs text-[#B0B8C4]">From: <span className="text-white">{opportunity.role_title || 'Role'}</span></p>}
                        {opportunity.org_chart_id && !opportunity.role_id && <p className="text-xs text-[#B0B8C4]">From: <span className="text-white">Org Chart</span></p>}
                      </div>
                      <button
                        onClick={() => {
                          if (opportunity.process_id) navigate(`/processes/${opportunity.process_id}`);
                          else if (opportunity.jd_id) navigate(`/job-descriptions/${opportunity.jd_id}`);
                          else if (opportunity.org_chart_id) navigate(`/org-chart`);
                        }}
                        className="text-xs text-[#818CF8] hover:text-white transition-colors flex items-center gap-1"
                      >
                        View <ArrowSquareOut size={12} />
                      </button>
                    </div>
                  </div>
                )}

                {/* Metadata */}
                <div className="bg-[#1E2836] rounded-xl border border-[rgba(255,255,255,0.08)] p-5">
                  <h3 className="text-sm font-semibold text-white mb-3">Details</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                    <div>
                      <p className="text-[#7D8896] mb-0.5">Category</p>
                      <p className="text-white">{opportunity.category || '—'}</p>
                    </div>
                    <div>
                      <p className="text-[#7D8896] mb-0.5">Created</p>
                      <p className="text-white">{new Date(opportunity.created_at).toLocaleDateString()}</p>
                    </div>
                    <div>
                      <p className="text-[#7D8896] mb-0.5">Updated</p>
                      <p className="text-white">{new Date(opportunity.updated_at).toLocaleDateString()}</p>
                    </div>
                    {opportunity.implemented_at && (
                      <div>
                        <p className="text-[#7D8896] mb-0.5">Implemented</p>
                        <p className="text-white">{new Date(opportunity.implemented_at).toLocaleDateString()}</p>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </div>

        {/* Edit ROI Modal */}
        {showEditROIModal && opportunity && (
          <EditROIModal
            isOpen={showEditROIModal}
            onClose={() => setShowEditROIModal(false)}
            opportunity={opportunity}
            onSave={handleSaveROI}
            organizationId={activeOrganization?.organization_id}
          />
        )}
      </>
  );
};

export default OpportunityDetail;
